import { motion } from "framer-motion";
import {
  Bot, LayoutDashboard, MessageSquare, Brain, Bell, Database,
  TrendingUp, Users, Clock, ThumbsUp, Circle, Send, FileBarChart2,
  ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

/* ── Dashboard Mockup ── */
function DashboardMockup() {
  const kpis = [
    { label: "Conversaciones", value: "142", icon: MessageSquare, color: "text-sky-500" },
    { label: "Leads generados", value: "89", icon: TrendingUp, color: "text-emerald-500" },
    { label: "Resp. promedio", value: "0:42s", icon: Clock, color: "text-violet-500" },
    { label: "Satisfacción", value: "96%", icon: ThumbsUp, color: "text-amber-500" },
  ];

  const conversations = [
    { name: "María García", topic: "Consulta sobre planes familiares", status: "IA", color: "bg-sky-400" },
    { name: "Carlos Rodríguez", topic: "Derivado a asesora · Luciana M.", status: "Asesor", color: "bg-emerald-400" },
    { name: "Ana Méndez", topic: "Preguntas sobre sede Pocitos", status: "IA", color: "bg-sky-400" },
  ];

  const bars = [18, 32, 28, 45, 38, 22, 35];
  const days = ["L", "M", "X", "J", "V", "S", "D"];

  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 shadow-2xl w-full">
      {/* Top bar */}
      <div className="bg-[#1F2937] px-4 py-2.5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-gray-400 text-xs">Sistema para Gyms · Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-[#38BDF8] rounded-full flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">A</span>
          </div>
          <span className="text-gray-400 text-xs">AEROBIC</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-4 gap-2">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="bg-white/5 rounded-xl p-2.5 border border-white/10">
              <kpi.icon size={13} className={`${kpi.color} mb-1.5`} />
              <p className="font-['Outfit'] text-base font-700 text-white leading-none">{kpi.value}</p>
              <p className="text-gray-500 text-[9px] mt-0.5 leading-tight">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <p className="text-gray-400 text-[10px] font-medium mb-2">Actividad esta semana</p>
          <div className="flex items-end gap-1.5 h-12">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-sm bg-[#38BDF8]/60"
                  style={{ height: `${h * 0.9}px` }}
                />
                <span className="text-gray-600 text-[8px]">{days[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Conversations */}
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
            <p className="text-gray-400 text-[10px] font-medium">Últimas conversaciones</p>
            <span className="text-[#38BDF8] text-[9px] cursor-pointer">Ver todas</span>
          </div>
          {conversations.map((c, i) => (
            <div key={i} className={`px-3 py-2 flex items-center gap-2 ${i < 2 ? "border-b border-white/5" : ""}`}>
              <div className={`w-6 h-6 ${c.color} rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0`}>
                {c.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-[10px] font-medium truncate">{c.name}</p>
                <p className="text-gray-500 text-[9px] truncate">{c.topic}</p>
              </div>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${c.status === "IA" ? "bg-sky-900/60 text-sky-400" : "bg-emerald-900/60 text-emerald-400"}`}>
                {c.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Conversations Mockup ── */
function ConversationsMockup() {
  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 h-full">
      <div className="bg-[#1F2937] px-3 py-2 border-b border-white/10">
        <p className="text-gray-400 text-[10px]">Conversaciones · Vista asesor</p>
      </div>
      <div className="flex h-[170px]">
        {/* List */}
        <div className="w-1/3 border-r border-white/10 p-2 space-y-1">
          {[
            { name: "Maria G.", active: true },
            { name: "Carlos R.", active: false },
            { name: "Ana M.", active: false },
          ].map((c) => (
            <div key={c.name} className={`px-2 py-1.5 rounded-lg flex items-center gap-1.5 cursor-pointer ${c.active ? "bg-[#38BDF8]/15" : "hover:bg-white/5"}`}>
              <Circle size={6} className={c.active ? "text-[#38BDF8]" : "text-gray-600"} fill="currentColor" />
              <span className="text-[10px] text-gray-300 truncate">{c.name}</span>
            </div>
          ))}
        </div>
        {/* Chat */}
        <div className="flex-1 flex flex-col p-2 gap-1.5">
          <div className="flex justify-end">
            <div className="bg-[#38BDF8]/20 text-sky-300 text-[9px] px-2 py-1 rounded-lg max-w-[80%]">
              Hola quiero info de planes
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white/10 text-gray-300 text-[9px] px-2 py-1 rounded-lg max-w-[80%]">
              Hola! Tenemos planes desde $800/mes...
            </div>
          </div>
          <div className="mt-auto pt-1 border-t border-white/10 flex gap-1">
            <div className="flex-1 bg-white/5 rounded text-[8px] text-gray-600 px-2 py-1">
              Escribe respuesta...
            </div>
            <button className="bg-[#38BDF8] text-white text-[8px] px-2 rounded">Enviar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Instructions Mockup ── */
function InstructionsMockup() {
  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 h-full">
      <div className="bg-[#1F2937] px-3 py-2 border-b border-white/10">
        <p className="text-gray-400 text-[10px]">Instrucciones del asistente</p>
      </div>
      <div className="p-3 space-y-2">
        {[
          { tag: "Activo", title: "Precios y planes" },
          { tag: "Activo", title: "Horarios y sedes" },
          { tag: "Activo", title: "Derivación a asesora" },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-2.5 py-2 border border-white/10">
            <div className="flex items-center gap-2">
              <Brain size={11} className="text-[#38BDF8]" />
              <span className="text-gray-300 text-[10px]">{item.title}</span>
            </div>
            <span className="bg-emerald-900/50 text-emerald-400 text-[8px] px-1.5 py-0.5 rounded-full">{item.tag}</span>
          </div>
        ))}
        <div className="bg-[#38BDF8]/5 border border-[#38BDF8]/20 rounded-lg p-2">
          <p className="text-[#38BDF8] text-[8px]">+ Nueva instrucción</p>
        </div>
      </div>
    </div>
  );
}

/* ── Notification Mockup ── */
function NotificationMockup() {
  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 h-full">
      <div className="bg-[#1F2937] px-3 py-2 border-b border-white/10">
        <p className="text-gray-400 text-[10px]">Notificación a asesora</p>
      </div>
      <div className="p-3">
        <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 bg-[#25D366] rounded-full flex items-center justify-center">
              <MessageSquare size={10} className="text-white" />
            </div>
            <span className="text-white text-[10px] font-semibold">WhatsApp Business</span>
          </div>
          <p className="text-gray-300 text-[10px] leading-relaxed">
            Nueva consulta asignada: <span className="text-[#25D366]">Maria Garcia</span>
            <br />Tema: Plan familiar · Interés: Alto
          </p>
          <div className="mt-2 bg-[#38BDF8]/20 rounded-lg px-2 py-1">
            <p className="text-[#38BDF8] text-[9px] underline">Ver conversacion →</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── CRM Mockup ── */
function CRMMockup() {
  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 h-full">
      <div className="bg-[#1F2937] px-3 py-2 border-b border-white/10 flex items-center gap-2">
        <Database size={10} className="text-[#38BDF8]" />
        <p className="text-gray-400 text-[10px]">Registro ABC EVO · CRM</p>
      </div>
      <div className="p-3 space-y-2">
        <div className="bg-white/5 border border-white/10 rounded-lg p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white text-[10px] font-medium">Maria Garcia</span>
            <span className="bg-emerald-900/50 text-emerald-400 text-[8px] px-1.5 py-0.5 rounded-full">Registrado</span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {[["Canal", "WhatsApp"], ["Interés", "Plan familiar"], ["Estado", "En seguimiento"]].map(([k, v]) => (
              <div key={k} className="bg-white/5 rounded px-1.5 py-1">
                <p className="text-gray-600 text-[8px]">{k}</p>
                <p className="text-gray-300 text-[9px]">{v}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-gray-600 text-[9px] text-center">Sincronización automática</p>
      </div>
    </div>
  );
}

/* ── Reports Mockup ── */
function ReportsMockup() {
  const linePoints = [12, 22, 18, 35, 28, 42, 38, 55, 48, 62, 58, 70];
  const maxVal = Math.max(...linePoints);
  const width = 220;
  const height = 52;
  const pts = linePoints.map((v, i) => {
    const x = (i / (linePoints.length - 1)) * width;
    const y = height - (v / maxVal) * height;
    return `${x},${y}`;
  }).join(" ");
  const areaBottom = `${width},${height} 0,${height}`;

  const metrics = [
    { label: "Tasa de conversión", value: "38%", up: true },
    { label: "Resueltas por IA", value: "74%", up: true },
    { label: "Tiempo de resp.", value: "0:42s", up: false },
    { label: "Derivaciones", value: "26%", up: false },
  ];

  return (
    <div className="bg-[#111827] rounded-2xl overflow-hidden border border-white/10 w-full">
      <div className="bg-[#1F2937] px-4 py-2.5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-gray-400 text-xs">Sistema para Gyms · Reportes</span>
        </div>
        <span className="text-gray-500 text-[10px] bg-white/5 px-2 py-0.5 rounded">Mayo 2025</span>
      </div>
      <div className="p-4 space-y-3">
        {/* Line chart */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-[10px] font-medium">Consultas recibidas este mes</p>
            <span className="text-emerald-400 text-[9px] flex items-center gap-0.5">
              <ArrowUpRight size={9} /> +18% vs mes anterior
            </span>
          </div>
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: "52px" }}>
            <defs>
              <linearGradient id="rpg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={`${pts} ${areaBottom}`} fill="url(#rpg)" />
            <polyline points={pts} fill="none" stroke="#38BDF8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {/* Metrics */}
        <div className="grid grid-cols-2 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className="bg-white/5 border border-white/10 rounded-lg p-2">
              <div className="flex items-center justify-between mb-0.5">
                <p className="text-gray-500 text-[8px]">{m.label}</p>
                {m.up
                  ? <ArrowUpRight size={9} className="text-emerald-400" />
                  : <ArrowDownRight size={9} className="text-sky-400" />}
              </div>
              <p className="font-['Outfit'] text-base font-700 text-white">{m.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const features = [
  {
    icon: LayoutDashboard,
    title: "Dashboard con KPIs en tiempo real",
    description: "Conversaciones activas, leads generados, tiempo de respuesta y satisfacción en un panel centralizado y actualizado al instante.",
    wide: true,
    mockup: <DashboardMockup />,
  },
  {
    icon: FileBarChart2,
    title: "Reportes y análisis",
    description: "Informes periódicos con las métricas clave del sistema: volumen de consultas, tasa de conversión, performance del asistente, tiempos de respuesta y más.",
    wide: true,
    mockup: <ReportsMockup />,
  },
  {
    icon: MessageSquare,
    title: "Gestión de conversaciones",
    description: "Monitoreo en vivo de todos los chats. El operador puede intervenir y tomar control en cualquier momento.",
    mockup: <ConversationsMockup />,
  },
  {
    icon: Brain,
    title: "Base de conocimiento e instrucciones",
    description: "Configurá el asistente con prompts específicos sobre planes, promociones y políticas de AEROBIC.",
    mockup: <InstructionsMockup />,
  },
  {
    icon: Bell,
    title: "Notificaciones a asesoras",
    description: "Cuando se deriva una consulta, la asesora recibe un WhatsApp con el link a la conversación para responder desde el sistema.",
    mockup: <NotificationMockup />,
  },
  {
    icon: Database,
    title: "Integración con ABC EVO",
    description: "Cada nuevo contacto y conversación se registra automáticamente en el CRM. El equipo puede continuar el proceso manualmente desde allí.",
    mockup: <CRMMockup />,
  },
  {
    icon: Send,
    title: "Mensajería proactiva (saliente)",
    description: "El sistema también envía mensajes: confirmaciones, recordatorios de vencimiento de plan, promociones y seguimientos automatizados.",
  },
  {
    icon: Bot,
    title: "Asistente IA en WhatsApp · 24/7",
    description: "Un número único de WhatsApp atiende a todos los socios y leads, a cualquier hora, con respuestas precisas e instantáneas. Parte del sistema, no el sistema completo.",
  },
];

export default function Features() {
  return (
    <section
      id="funcionalidades"
      data-testid={LANDING.featuresSection}
      className="bg-white py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mb-14"
        >
          <span className="inline-block text-[#0EA5E9] text-sm font-semibold tracking-widest uppercase mb-3">
            Funcionalidades
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-700 text-gray-900 tracking-tight max-w-lg">
              Un sistema completo, no solo un bot
            </h2>
            <p className="text-gray-500 text-sm max-w-xs">
              Atención, gestión, reportes y automatización en un mismo lugar.
            </p>
          </div>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feat, i) => (
            <motion.div
              key={feat.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`border border-gray-200 rounded-2xl overflow-hidden card-hover bg-white ${
                feat.wide ? "lg:col-span-2" : ""
              }`}
            >
              {/* Mockup area */}
              {feat.mockup && (
                <div className="p-4 bg-gray-900">
                  {feat.mockup}
                </div>
              )}

              {/* Text area */}
              <div className="p-5">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 bg-sky-50 rounded-lg flex items-center justify-center">
                    <feat.icon size={16} className="text-[#38BDF8]" />
                  </div>
                  <h3 className="font-['Outfit'] text-base font-600 text-gray-900">
                    {feat.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {feat.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
